import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  CssBaseline,
  FormControl,
  InputLabel,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import LinkIcon from "@material-ui/icons/Link";
import LockOpenIcon from "@material-ui/icons/LockOpen";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import VpnKeyIcon from "@material-ui/icons/VpnKey";

import cchatLogoIcon from "../../assets/cchat-logo-icon.svg";
import cchatRobotGuide from "../../assets/cchat-robot-guide.png";
import api from "../../services/api";

export const useStyles = makeStyles(theme => ({
  root: {
    minHeight: "100vh",
    background:
      "radial-gradient(circle at 18% 18%, rgba(0, 89, 218, 0.13) 0, transparent 32%), linear-gradient(135deg, #edf4ff 0%, #f8fbff 48%, #eef3f8 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
    color: "#10233d"
  },
  shell: {
    width: "100%",
    maxWidth: 940,
    minHeight: 560,
    display: "grid",
    gridTemplateColumns: "360px minmax(0, 1fr)",
    borderRadius: 22,
    border: "1px solid #c9d8eb",
    boxShadow: "0 26px 70px rgba(16, 35, 61, 0.18)",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      maxWidth: 720,
      gridTemplateColumns: "1fr"
    },
    [theme.breakpoints.down("xs")]: {
      minHeight: "auto",
      borderRadius: 18
    }
  },
  heroPanel: {
    position: "relative",
    color: "#ffffff",
    background: "linear-gradient(155deg, #1548a5 0%, #0059DA 54%, #0e7adf 100%)",
    padding: theme.spacing(3.25),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    overflow: "hidden",
    "&:after": {
      content: '""',
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
      height: 4,
      background: "#1bc5bd"
    },
    [theme.breakpoints.down("sm")]: {
      minHeight: 310,
      padding: theme.spacing(3)
    }
  },
  heroGlow: {
    position: "absolute",
    width: 260,
    height: 260,
    right: -92,
    top: -96,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.12)"
  },
  brandRow: {
    position: "relative",
    zIndex: 2,
    display: "flex",
    gap: theme.spacing(1.6),
    alignItems: "center"
  },
  brandMark: {
    width: 66,
    height: 66,
    minWidth: 66,
    borderRadius: 18,
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid rgba(255,255,255,0.52)",
    boxShadow: "0 14px 26px rgba(15, 23, 42, 0.18)",
    position: "relative"
  },
  heroBrandIcon: {
    width: 50,
    height: 50,
    display: "block"
  },
  heroStateBadge: {
    width: 25,
    height: 25,
    borderRadius: 999,
    position: "absolute",
    right: -7,
    bottom: -7,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "2px solid #ffffff",
    boxShadow: "0 8px 16px rgba(16, 35, 61, 0.18)",
    "& svg": {
      width: 14,
      height: 14,
      color: "#ffffff"
    }
  },
  heroCopy: {
    minWidth: 0
  },
  heroMascot: {
    position: "relative",
    zIndex: 1,
    flex: 1,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    marginTop: theme.spacing(2),
    minHeight: 206,
    "&::before": {
      content: '""',
      position: "absolute",
      left: "50%",
      bottom: 10,
      width: 168,
      height: 26,
      borderRadius: "50%",
      background: "radial-gradient(ellipse at center, rgba(0, 16, 58, 0.40) 0%, rgba(0, 16, 58, 0.22) 48%, rgba(0, 16, 58, 0) 74%)",
      filter: "blur(8px)",
      transform: "translateX(-50%) scale(0.96)",
      animation: "$robotShadowFloat 4.8s ease-in-out infinite",
      pointerEvents: "none"
    },
    [theme.breakpoints.down("sm")]: {
      minHeight: 156,
      justifyContent: "flex-end",
      "&::before": {
        left: "calc(100% - 84px)",
        right: "auto",
        width: 126,
        height: 21
      }
    }
  },
  robotImage: {
    position: "relative",
    zIndex: 2,
    width: "min(194px, 68%)",
    maxHeight: 230,
    objectFit: "contain",
    imageRendering: "auto",
    filter: "none",
    transform: "translate3d(0, 6px, 0)",
    transformOrigin: "50% 82%",
    animation: "$robotFloat 4.8s ease-in-out infinite",
    willChange: "transform",
    [theme.breakpoints.down("sm")]: {
      width: 156,
      maxHeight: 184,
      position: "absolute",
      right: 4,
      bottom: -10
    },
    [theme.breakpoints.down("xs")]: {
      opacity: 0.5,
      right: -20
    }
  },
  stepsBox: {
    position: "relative",
    zIndex: 2,
    display: "grid",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2)
  },
  stepItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minHeight: 38,
    borderRadius: 13,
    padding: theme.spacing(0.85, 1),
    background: "rgba(255,255,255,0.12)",
    border: "1px solid rgba(255,255,255,0.16)",
    color: "#ffffff",
    fontSize: 12,
    fontWeight: 800
  },
  stepNumber: {
    width: 22,
    height: 22,
    minWidth: 22,
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#ffffff",
    color: "#0059DA",
    fontSize: 12,
    fontWeight: 900
  },
  "@keyframes robotFloat": {
    "0%, 100%": {
      transform: "translate3d(0, 6px, 0)"
    },
    "50%": {
      transform: "translate3d(0, -4px, 0)"
    }
  },
  "@keyframes robotShadowFloat": {
    "0%, 100%": {
      opacity: 0.82,
      transform: "translateX(-50%) scale(0.96)"
    },
    "50%": {
      opacity: 0.52,
      transform: "translateX(-50%) scale(0.76)"
    }
  },
  contentPanel: {
    background: "#fbfdff",
    padding: theme.spacing(4),
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2.5)
    }
  },
  panelTitle: {
    margin: 0,
    color: "#10233d",
    fontSize: 22,
    fontWeight: 900,
    lineHeight: 1.16,
    [theme.breakpoints.down("xs")]: {
      fontSize: 20
    }
  },
  panelSubtitle: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2.4),
    color: "#506278",
    lineHeight: 1.5,
    fontSize: 14.5
  },
  header: {
    background: "linear-gradient(135deg, #1548a5 0%, #0059DA 58%, #1b6fd7 100%)",
    color: "#ffffff",
    padding: theme.spacing(2.75, 3),
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    borderBottom: "3px solid #1bc5bd",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2.25),
      alignItems: "flex-start"
    }
  },
  headerIcon: {
    width: 62,
    height: 62,
    minWidth: 62,
    borderRadius: 16,
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid rgba(255,255,255,0.58)",
    boxShadow: "0 14px 26px rgba(15, 23, 42, 0.18)",
    position: "relative",
    [theme.breakpoints.down("xs")]: {
      width: 56,
      height: 56,
      minWidth: 56
    }
  },
  brandIcon: {
    width: 48,
    height: 48,
    display: "block",
    [theme.breakpoints.down("xs")]: {
      width: 42,
      height: 42
    }
  },
  stateBadge: {
    width: 25,
    height: 25,
    borderRadius: 999,
    position: "absolute",
    right: -7,
    bottom: -7,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "2px solid #ffffff",
    boxShadow: "0 8px 16px rgba(16, 35, 61, 0.18)",
    "& svg": {
      width: 14,
      height: 14,
      color: "#ffffff"
    }
  },
  stateBadgeLogin: {
    background: "#EE9D01"
  },
  stateBadgeLink: {
    background: "#0059DA"
  },
  stateBadgeSuccess: {
    background: "#1f8a73"
  },
  headerText: {
    minWidth: 0
  },
  eyebrow: {
    margin: 0,
    color: "rgba(255,255,255,0.82)",
    fontSize: 11,
    fontWeight: 800,
    lineHeight: 1.2,
    textTransform: "uppercase"
  },
  title: {
    margin: theme.spacing(0.45, 0, 0.8),
    color: "#ffffff",
    fontSize: 24,
    fontWeight: 850,
    lineHeight: 1.14,
    [theme.breakpoints.down("xs")]: {
      fontSize: 21
    }
  },
  modePill: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 24,
    borderRadius: 999,
    padding: "4px 10px",
    background: "rgba(255,255,255,0.14)",
    border: "1px solid rgba(255,255,255,0.22)",
    color: "#ffffff",
    fontSize: 12,
    fontWeight: 800
  },
  body: {
    padding: theme.spacing(3),
    background: "#fbfdff",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2.25)
    }
  },
  helper: {
    color: "#506278",
    lineHeight: 1.5,
    marginBottom: theme.spacing(2.5)
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2)
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr"
    }
  },
  inputField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      background: "#ffffff",
      color: "#10233d",
      "& fieldset": {
        borderColor: "#c9d8eb"
      },
      "&:hover fieldset": {
        borderColor: "#2b6cb0"
      },
      "&.Mui-focused fieldset": {
        borderColor: "#0059DA",
        borderWidth: 1
      }
    },
    "& .MuiInputLabel-outlined": {
      color: "#607082",
      fontWeight: 700
    },
    "& .MuiInputLabel-outlined.Mui-focused": {
      color: "#0059DA"
    },
    "& .MuiInputAdornment-root svg": {
      color: "#2b6cb0"
    }
  },
  selectControl: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      background: "#ffffff",
      color: "#10233d",
      "& fieldset": {
        borderColor: "#c9d8eb"
      },
      "&:hover fieldset": {
        borderColor: "#2b6cb0"
      },
      "&.Mui-focused fieldset": {
        borderColor: "#0059DA",
        borderWidth: 1
      }
    },
    "& .MuiInputLabel-outlined": {
      color: "#607082",
      fontWeight: 700
    },
    "& .MuiInputLabel-outlined.Mui-focused": {
      color: "#0059DA"
    }
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2.25),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column"
    }
  },
  primaryButton: {
    minHeight: 46,
    borderRadius: 12,
    background: "linear-gradient(135deg, #0059DA 0%, #1548a5 100%)",
    color: "#ffffff",
    fontWeight: 800,
    letterSpacing: 0,
    textTransform: "none",
    boxShadow: "0 10px 20px rgba(0, 89, 218, 0.24)",
    "&:hover": {
      background: "linear-gradient(135deg, #004dbe 0%, #123f92 100%)",
      boxShadow: "0 12px 22px rgba(0, 89, 218, 0.28)"
    },
    "& svg": {
      color: "#ffffff"
    }
  },
  secondaryButton: {
    minHeight: 46,
    borderRadius: 12,
    fontWeight: 800,
    letterSpacing: 0,
    textTransform: "none",
    color: "#10233d",
    borderColor: "#c9d8eb",
    background: "#ffffff",
    "&:hover": {
      color: "#d93f4f",
      borderColor: "#d93f4f",
      background: "#fff7f8"
    }
  },
  alert: {
    background: "#fff7e6",
    color: "#8a5b00",
    border: "1px solid #ffd58a",
    borderRadius: 12,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2)
  },
  success: {
    background: "#e8fff6",
    color: "#17604f",
    border: "1px solid #a7ecd6",
    borderRadius: 12,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2)
  },
  connectionMeta: {
    marginTop: theme.spacing(1.75),
    padding: theme.spacing(1.75),
    borderRadius: 12,
    background: "#f4f8ff",
    color: "#223142",
    border: "1px solid #d6e0eb"
  },
  connectionMetaTitle: {
    margin: 0,
    color: "#10233d",
    fontSize: 13,
    fontWeight: 850
  },
  metaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.25),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr"
    }
  },
  metaItem: {
    borderRadius: 10,
    background: "#ffffff",
    border: "1px solid #dce7f4",
    padding: theme.spacing(1.15),
    minWidth: 0,
    "& span": {
      display: "block",
      color: "#607082",
      fontSize: 11,
      fontWeight: 800,
      textTransform: "uppercase"
    },
    "& strong": {
      display: "block",
      marginTop: 4,
      color: "#10233d",
      fontSize: 13,
      fontWeight: 850,
      overflowWrap: "anywhere"
    }
  },
  loadingBox: {
    minHeight: 230,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    color: "#607082"
  },
  loadingLogo: {
    width: 54,
    height: 54,
    display: "block"
  }
}));

const parseToken = () => {
  try {
    const stored = localStorage.getItem("token");
    return stored ? JSON.parse(stored) : "";
  } catch (_) {
    return "";
  }
};

const getRequestErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (data?.error?.code === "ERR_AUTOMATION_HTTPS_REQUIRED") {
    return "O CChat não reconheceu a conexão segura desta integração. Entre em contato com o suporte.";
  }
  const apiMessage = [data?.error, data?.message].find(value => typeof value === "string" && value.trim());
  if (apiMessage) {
    return apiMessage;
  }

  const message = typeof error?.message === "string" ? error.message : "";
  if (message === "Network Error") {
    return "Não foi possível falar com a API do CChat. Confira se o backend está ativo e se a URL de rede foi liberada.";
  }

  return message ? `${fallback} (${message})` : fallback;
};

const CControleLink = () => {
  const classes = useStyles();
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const automationMode = params.get("purpose") === "automations";
  const deliveryMode = params.get("purpose") === "deliveryok-link";
  const targetOrigin = params.get("origin") || "*";
  const mode = params.get("mode") || "CChat";
  const apiBaseUrl = (params.get("apiBase") || "").replace(/\/+$/, "");

  const [login, setLogin] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [connections, setConnections] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedWhatsappId, setSelectedWhatsappId] = useState("");
  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState(false);
  const [automationOptions, setAutomationOptions] = useState(null);
  const [companyScopeConfirmed, setCompanyScopeConfirmed] = useState(false);

  const selectedConnection = connections.find(connection => String(connection.id) === String(selectedWhatsappId));
  const stateIcon = success ? <CheckCircleIcon /> : authenticated ? <LinkIcon /> : <LockOpenIcon />;
  const stateBadgeClass = [
    classes.heroStateBadge,
    success ? classes.stateBadgeSuccess : authenticated ? classes.stateBadgeLink : classes.stateBadgeLogin
  ].join(" ");

  useEffect(() => {
    if (apiBaseUrl && !automationMode && !deliveryMode) {
      api.defaults.baseURL = apiBaseUrl;
    }
  }, [apiBaseUrl, automationMode, deliveryMode]);

  const storeSession = data => {
    if (!data?.token) return;
    localStorage.setItem("token", JSON.stringify(data.token));
    api.defaults.headers.Authorization = `Bearer ${data.token}`;
  };

  const loadOptions = async () => {
    if (automationMode) {
      try {
        const { data: result } = await api.get("/integration-automation-grants/options", { params: {
          consumerInstance: params.get("consumerInstance"), environment: params.get("environment"),
          origin: params.get("origin"), state: params.get("state")
        } });
        const options = result.data;
        setAutomationOptions(options);
        setConnections(options.connections || []);
        setSelectedWhatsappId("");
        setUsers([]);
      } catch (_) {
        setAutomationOptions(null); setConnections([]);
        setMessage("Não foi possível autorizar esta solicitação. Abra novamente a integração pelo DeliveryOK e confira sua permissão de administrador.");
      }
      return;
    }
    const { data } = await api.get("/ccontrole-link/options");
    const availableConnections = data.connections || [];
    const availableUsers = data.users || [];
    const defaultConnection =
      availableConnections.find(connection => connection.isDefault) ||
      availableConnections.find(connection => connection.status === "CONNECTED") ||
      availableConnections[0];

    setConnections(availableConnections);
    setUsers(availableUsers);
    setSelectedWhatsappId(defaultConnection ? String(defaultConnection.id) : "");
  };

  useEffect(() => {
    let mounted = true;

    const boot = async () => {
      const storedToken = parseToken();

      if (!storedToken) {
        if (mounted) setLoading(false);
        return;
      }

      api.defaults.headers.Authorization = `Bearer ${storedToken}`;

      try {
        const { data } = await api.post("/auth/refresh_token");
        if (!mounted) return;
        storeSession(data);
        setUser(data.user);
        setAuthenticated(true);
        await loadOptions();
      } catch (_) {
        localStorage.removeItem("token");
        api.defaults.headers.Authorization = undefined;
      } finally {
        if (mounted) setLoading(false);
      }
    };

    boot();

    return () => {
      mounted = false;
    };
  }, []);

  const handleLogin = async event => {
    event.preventDefault();
    setMessage("");
    setWorking(true);

    try {
      const { data } = await api.post("/auth/login", login);
      storeSession(data);
      setUser(data.user);
      setAuthenticated(true);
      await loadOptions();
    } catch (error) {
      setMessage(getRequestErrorMessage(error, "Não foi possível entrar no CChat."));
    } finally {
      setWorking(false);
      setLoading(false);
    }
  };

  const authorize = async () => {
    if (!selectedWhatsappId) {
      setMessage("Selecione uma conexão do CChat antes de vincular.");
      return;
    }

    setMessage("");
    setWorking(true);

    try {
      if (automationMode) {
        if (!automationOptions || !companyScopeConfirmed) {
          setMessage("Confirme a autorização de leitura das mensagens desta empresa.");
          return;
        }
        const { data: result } = await api.post("/integration-automation-grants", {
          whatsappId: Number(selectedWhatsappId), consumerInstance: automationOptions.consumerInstance,
          environment: automationOptions.environment, capabilities: ["automation:read"], confirmCompanyScope: true
        });
        if (window.opener) {
          window.opener.postMessage({ type: "cchat:automation-authorized", source: "cchat", ok: true,
            state: automationOptions.state, grant: result.data }, automationOptions.consumerOrigin);
        }
        setSuccess(true); setMessage("Leitura autorizada para o DeliveryOK. Você pode fechar esta janela.");
        return;
      }
      const { data } = await api.post(deliveryMode ? "/integration-automation-link" : "/ccontrole-link/authorize", {
        whatsappId: Number(selectedWhatsappId),
        ...(deliveryMode ? { consumerInstance: params.get("consumerInstance"), environment: params.get("environment"), origin: targetOrigin, state: params.get("state") } : {})
      });

      const selectedUser = users.find(item => String(item.id) === String(user?.id)) || users[0] || null;

      const payload = {
        type: "ccontrole:cchat-linked",
        source: "cchat",
        ok: true,
        companyId: data.companyId,
        token: data.token,
        whatsapp: data.whatsapp,
        users: data.users || users,
        selectedUserId: data.selectedUserId || (selectedUser ? selectedUser.id : ""),
        ...(deliveryMode ? { management: data.management, managementContract: data.managementContract, state: params.get("state") } : {})
      };

      if (window.opener) {
        window.opener.postMessage(payload, deliveryMode ? data.management.consumerOrigin : targetOrigin);
      }

      setSuccess(true);
      setMessage(deliveryMode ? "Cchat conectado ao DeliveryOK. A gestão das mensagens já está liberada." : "Vínculo enviado para o CControle. Esta janela será fechada.");

      window.setTimeout(() => {
        window.close();
      }, 900);
    } catch (error) {
      setMessage(automationMode ? "Não foi possível confirmar a autorização. Atualize a integração e tente novamente." : getRequestErrorMessage(error, deliveryMode ? "Não foi possível concluir o vínculo com o DeliveryOK." : "Não foi possível concluir o vínculo com o CControle."));
    } finally {
      setWorking(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    api.defaults.headers.Authorization = undefined;
    setAuthenticated(false);
    setUser(null);
    setConnections([]);
    setUsers([]);
    setSelectedWhatsappId("");
    setAutomationOptions(null);
    setCompanyScopeConfirmed(false);
    setSuccess(false);
    setMessage("Sessao encerrada. Entre com a conta correta do CChat.");
    setLogin({ email: "", password: "" });
  };

  return (
    <div className={classes.root}>
      <CssBaseline />
      <Paper className={classes.shell}>
        <aside className={classes.heroPanel}>
          <span className={classes.heroGlow} />
          <div className={classes.brandRow}>
            <div className={classes.brandMark}>
              <img className={classes.heroBrandIcon} src={cchatLogoIcon} alt="CChat" />
              <span className={stateBadgeClass}>{stateIcon}</span>
            </div>
            <Box className={classes.heroCopy}>
              <Typography className={classes.eyebrow}>Integração CChat</Typography>
              <Typography component="h1" className={classes.title}>{automationMode ? "Mensagens no DeliveryOK" : deliveryMode ? "Conectar CChat ao DeliveryOK" : "Conectar CChat ao CControle"}</Typography>
              <span className={classes.modePill}>Ambiente: {mode}</span>
            </Box>
          </div>

          <div className={classes.heroMascot}>
            <img className={classes.robotImage} src={cchatRobotGuide} alt="Assistente CChat" />
          </div>

          <div className={classes.stepsBox}>
            <div className={classes.stepItem}>
              <span className={classes.stepNumber}>1</span>
              Entre com seu usuário CChat
            </div>
            <div className={classes.stepItem}>
              <span className={classes.stepNumber}>2</span>
              Escolha a conexão do WhatsApp
            </div>
            <div className={classes.stepItem}>
              <span className={classes.stepNumber}>3</span>
              {automationMode ? "Autorize a leitura das mensagens" : deliveryMode ? "Gerencie suas mensagens no DeliveryOK" : "O CControle sincroniza sozinho"}
            </div>
          </div>
        </aside>

        <main className={classes.contentPanel}>
          {loading ? (
            <Box className={classes.loadingBox}>
              <img className={classes.loadingLogo} src={cchatLogoIcon} alt="CChat" />
              <CircularProgress size={30} />
              <Typography variant="body2">Preparando vínculo...</Typography>
            </Box>
          ) : (
            <>
              {message && (
                <div className={success ? classes.success : classes.alert}>
                  {message}
                </div>
              )}

              {!authenticated ? (
                <>
                  <Typography component="h2" className={classes.panelTitle}>Acesse sua conta CChat</Typography>
                  <Typography className={classes.panelSubtitle}>
                    {automationMode ? "Entre como administrador para autorizar a consulta das mensagens pelo DeliveryOK." : deliveryMode ? "Entre como administrador e escolha sua conexão. O vínculo já libera a gestão no DeliveryOK." : "Depois do login, você escolhe a conexão e o CControle preenche o vínculo automaticamente."}
                  </Typography>
                  <form className={classes.form} onSubmit={handleLogin}>
                    <div className={classes.formGrid}>
                      <TextField
                        className={classes.inputField}
                        label="E-mail"
                        id="cchat-link-email"
                        variant="outlined"
                        value={login.email}
                        onChange={event => setLogin({ ...login, email: event.target.value })}
                        autoFocus
                        fullWidth
                        required
                        autoComplete="username"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <MailOutlineIcon />
                            </InputAdornment>
                          )
                        }}
                      />
                      <TextField
                        className={classes.inputField}
                        label="Senha"
                        id="cchat-link-password"
                        variant="outlined"
                        type="password"
                        value={login.password}
                        onChange={event => setLogin({ ...login, password: event.target.value })}
                        fullWidth
                        required
                        autoComplete="current-password"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <VpnKeyIcon />
                            </InputAdornment>
                          )
                        }}
                      />
                    </div>
                    <div className={classes.actions}>
                      <Button
                        type="submit"
                        className={classes.primaryButton}
                        variant="contained"
                        fullWidth
                        disabled={working}
                        startIcon={working ? <CircularProgress size={18} color="inherit" /> : <LockOpenIcon />}
                      >
                        Entrar e continuar
                      </Button>
                    </div>
                  </form>
                </>
              ) : (
                <>
                  <Typography component="h2" className={classes.panelTitle}>
                    Login confirmado{user?.name ? `, ${user.name}` : ""}
                  </Typography>
                  <Typography className={classes.panelSubtitle}>
                    {automationMode ? "Selecione a conexão já vinculada ao DeliveryOK. Esta autorização permite consultar as mensagens cadastradas." : deliveryMode ? "Selecione a conexão para gerenciar mensagens e enviar os avisos dos pedidos pelo DeliveryOK." : "Selecione a conexão que o CControle deve usar para enviar mensagens, catálogos e retornos automáticos."}
                  </Typography>
                  <FormControl className={classes.selectControl} variant="outlined" fullWidth>
                    <InputLabel id="ccontrole-whatsapp-label">Conexão CChat</InputLabel>
                    <Select
                      labelId="ccontrole-whatsapp-label"
                      label="Conexão CChat"
                      value={selectedWhatsappId}
                      onChange={event => setSelectedWhatsappId(event.target.value)}
                    >
                      {connections.length ? (
                        connections.map(connection => (
                          <MenuItem key={connection.id} value={String(connection.id)}>
                            {connection.name} {connection.status ? `- ${connection.status}` : ""}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value="" disabled>Nenhuma conexão disponível</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                  {automationMode && (
                    <FormControlLabel control={<Checkbox color="primary" checked={companyScopeConfirmed}
                      onChange={event => setCompanyScopeConfirmed(event.target.checked)} />}
                      label="Autorizo o DeliveryOK a consultar as mensagens desta empresa. As regras por palavra-chave são compartilhadas pelas conexões da empresa." />
                  )}

                  {selectedConnection && (
                    <div className={classes.connectionMeta}>
                      <p className={classes.connectionMetaTitle}>Conexão selecionada</p>
                      <div className={classes.metaGrid}>
                        <div className={classes.metaItem}>
                          <span>Empresa CChat</span>
                          <strong>{user?.companyId || "-"}</strong>
                        </div>
                        <div className={classes.metaItem}>
                          <span>Conexão</span>
                          <strong>{selectedConnection.name}</strong>
                        </div>
                        <div className={classes.metaItem}>
                          <span>{automationMode ? "Permissão" : "Usuários"}</span>
                          <strong>{automationMode ? "Somente leitura" : users.length}</strong>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className={classes.actions}>
                    <Button
                      type="button"
                      className={classes.primaryButton}
                      variant="contained"
                      fullWidth
                      disabled={working || success || !selectedWhatsappId || (automationMode && (!automationOptions || !companyScopeConfirmed))}
                      onClick={authorize}
                      startIcon={working ? <CircularProgress size={18} color="inherit" /> : <LinkIcon />}
                    >
                      {automationMode ? "Autorizar leitura no DeliveryOK" : deliveryMode ? "Vincular ao DeliveryOK" : "Vincular ao CControle"}
                    </Button>
                    <Button
                      type="button"
                      className={classes.secondaryButton}
                      variant="outlined"
                      fullWidth
                      disabled={working}
                      onClick={handleLogout}
                    >
                      Sair da conta
                    </Button>
                  </div>
                </>
              )}
            </>
          )}
        </main>
      </Paper>
    </div>
  );
};

export default CControleLink;
